/**
 * Table Subfolder Factories - Functions for creating table subfolder nodes
 */

import {
  createColumnsFolderNodeId,
  createKeysFolderNodeId,
  createConstraintsFolderNodeId,
  createTriggersFolderNodeId,
  createIndexesFolderNodeId,
  createStatisticsFolderNodeId,
  createPartitionsFolderNodeId,
  createColumnNodeId,
  createKeyNodeId,
  createConstraintNodeId,
  createIndexNodeId,
  createTriggerNodeId,
  createStatisticNodeId,
  createPartitionNodeId,
} from '../utils/nodeIdUtils';
import {
  ColumnsFolderNode,
  KeysFolderNode,
  ConstraintsFolderNode,
  TriggersFolderNode,
  IndexesFolderNode,
  StatisticsFolderNode,
  PartitionsFolderNode,
  TableSubfolderNode,
  ColumnTreeNode,
  ColumnNodeMetadata,
  KeyTreeNode,
  KeyNodeMetadata,
  ConstraintTreeNode,
  ConstraintNodeMetadata,
  IndexTreeNode,
  IndexNodeMetadata,
  TriggerTreeNode,
  TriggerNodeMetadata,
  StatisticTreeNode,
  StatisticNodeMetadata,
  PartitionTreeNode,
  PartitionNodeMetadata,
} from './nodeTypes';

/**
 * Creates a columns folder node under a table
 */
export function createColumnsFolderNode(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  parentNodeId: string
): ColumnsFolderNode {
  return {
    id: createColumnsFolderNodeId(connectionId, database, schema, tableName),
    parentId: parentNodeId,
    name: 'Columns',
    type: 'columns-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      schema,
      tableName
    }
  };
}

/**
 * Creates a keys folder node under a table
 */
export function createKeysFolderNode(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  parentNodeId: string
): KeysFolderNode {
  return {
    id: createKeysFolderNodeId(connectionId, database, schema, tableName),
    parentId: parentNodeId,
    name: 'Keys',
    type: 'keys-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      schema,
      tableName
    }
  };
}

/**
 * Creates a constraints folder node under a table
 */
export function createConstraintsFolderNode(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  parentNodeId: string
): ConstraintsFolderNode {
  return {
    id: createConstraintsFolderNodeId(connectionId, database, schema, tableName),
    parentId: parentNodeId,
    name: 'Constraints',
    type: 'constraints-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      schema,
      tableName
    }
  };
}

/**
 * Creates a triggers folder node under a table
 */
export function createTriggersFolderNode(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  parentNodeId: string
): TriggersFolderNode {
  return {
    id: createTriggersFolderNodeId(connectionId, database, schema, tableName),
    parentId: parentNodeId,
    name: 'Triggers',
    type: 'triggers-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      schema,
      tableName
    }
  };
}

/**
 * Creates an indexes folder node under a table
 */
export function createIndexesFolderNode(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  parentNodeId: string
): IndexesFolderNode {
  return {
    id: createIndexesFolderNodeId(connectionId, database, schema, tableName),
    parentId: parentNodeId,
    name: 'Indexes',
    type: 'indexes-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      schema,
      tableName
    }
  };
}

/**
 * Creates a statistics folder node under a table
 */
export function createStatisticsFolderNode(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  parentNodeId: string
): StatisticsFolderNode {
  return {
    id: createStatisticsFolderNodeId(connectionId, database, schema, tableName),
    parentId: parentNodeId,
    name: 'Statistics',
    type: 'statistics-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      schema,
      tableName
    }
  };
}

/**
 * Creates a partitions folder node under a partitioned table
 */
export function createPartitionsFolderNode(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  parentNodeId: string
): PartitionsFolderNode {
  return {
    id: createPartitionsFolderNodeId(connectionId, database, schema, tableName),
    parentId: parentNodeId,
    name: 'Partitions',
    type: 'partitions-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      schema,
      tableName
    }
  };
}

/**
 * Creates all standard subfolders under a table.
 * When `isPartitioned` is true (pgsql declarative-partitioned parent,
 * relkind = 'p'), a Partitions folder is appended — regular tables never get it.
 */
export function createTableSubfolders(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  parentNodeId: string,
  isPartitioned = false
): TableSubfolderNode[] {
  const subfolders: TableSubfolderNode[] = [
    createColumnsFolderNode(connectionId, database, schema, tableName, parentNodeId),
    createKeysFolderNode(connectionId, database, schema, tableName, parentNodeId),
    createConstraintsFolderNode(connectionId, database, schema, tableName, parentNodeId),
    createTriggersFolderNode(connectionId, database, schema, tableName, parentNodeId),
    createIndexesFolderNode(connectionId, database, schema, tableName, parentNodeId),
    createStatisticsFolderNode(connectionId, database, schema, tableName, parentNodeId)
  ];
  if (isPartitioned) {
    subfolders.push(
      createPartitionsFolderNode(connectionId, database, schema, tableName, parentNodeId)
    );
  }
  return subfolders;
}

/**
 * Creates a column tree node
 */
export function createColumnNode(
  metadata: ColumnNodeMetadata,
  parentNodeId: string
): ColumnTreeNode {
  return {
    id: createColumnNodeId(
      metadata.connectionId,
      metadata.database,
      metadata.schema,
      metadata.tableName,
      metadata.columnName
    ),
    parentId: parentNodeId,
    name: metadata.columnName,
    type: 'column',
    canHaveChildren: false,
    children: [],
    isExpanded: false,
    metadata
  };
}

/**
 * Creates a key tree node (PK/FK/UNIQUE)
 */
export function createKeyNode(
  metadata: KeyNodeMetadata,
  parentNodeId: string
): KeyTreeNode {
  return {
    id: createKeyNodeId(
      metadata.connectionId,
      metadata.database,
      metadata.schema,
      metadata.tableName,
      metadata.keyName
    ),
    parentId: parentNodeId,
    name: metadata.keyName,
    type: 'key',
    canHaveChildren: false,
    children: [],
    isExpanded: false,
    metadata
  };
}

/**
 * Creates a constraint tree node (CHECK/DEFAULT)
 */
export function createConstraintNode(
  metadata: ConstraintNodeMetadata,
  parentNodeId: string
): ConstraintTreeNode {
  return {
    id: createConstraintNodeId(
      metadata.connectionId,
      metadata.database,
      metadata.schema,
      metadata.tableName,
      metadata.constraintName
    ),
    parentId: parentNodeId,
    name: metadata.constraintName,
    type: 'constraint',
    canHaveChildren: false,
    children: [],
    isExpanded: false,
    metadata
  };
}

/**
 * Creates an index tree node
 */
export function createIndexNode(
  metadata: IndexNodeMetadata,
  parentNodeId: string
): IndexTreeNode {
  return {
    id: createIndexNodeId(
      metadata.connectionId,
      metadata.database,
      metadata.schema,
      metadata.tableName,
      metadata.indexName
    ),
    parentId: parentNodeId,
    name: metadata.indexName,
    type: 'index',
    canHaveChildren: false,
    children: [],
    isExpanded: false,
    metadata
  };
}

/**
 * Creates a trigger tree node
 */
export function createTriggerNode(
  metadata: TriggerNodeMetadata,
  parentNodeId: string
): TriggerTreeNode {
  return {
    id: createTriggerNodeId(
      metadata.connectionId,
      metadata.database,
      metadata.schema,
      metadata.tableName,
      metadata.triggerName
    ),
    parentId: parentNodeId,
    name: metadata.triggerName,
    type: 'trigger',
    canHaveChildren: false,
    children: [],
    isExpanded: false,
    metadata
  };
}

/**
 * Creates a statistic tree node
 */
export function createStatisticNode(
  metadata: StatisticNodeMetadata,
  parentNodeId: string
): StatisticTreeNode {
  return {
    id: createStatisticNodeId(
      metadata.connectionId,
      metadata.database,
      metadata.schema,
      metadata.tableName,
      metadata.statisticName
    ),
    parentId: parentNodeId,
    name: metadata.statisticName,
    type: 'statistic',
    canHaveChildren: false,
    children: [],
    isExpanded: false,
    metadata
  };
}

/**
 * Creates a partition leaf node (child of a Partitions folder). Leaf-level in
 * v1 — no subfolders under a partition. The bound clause is rendered into the
 * display name by the loader (mirrors the index display-name pattern).
 */
export function createPartitionNode(
  metadata: PartitionNodeMetadata,
  parentNodeId: string
): PartitionTreeNode {
  return {
    id: createPartitionNodeId(
      metadata.connectionId,
      metadata.database,
      metadata.schema,
      metadata.tableName,
      metadata.partitionName
    ),
    parentId: parentNodeId,
    name: metadata.partitionName,
    type: 'partition',
    canHaveChildren: false,
    children: [],
    isExpanded: false,
    metadata
  };
}
